import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

// Pick one project, several, or none (= all projects). Used by the Workspace board filter.

export interface ProjectOption {
  id: string;
  name: string;
  key?: string;
  /** Shown greyed out and not selectable (e.g. a team without boards) */
  disabled?: boolean;
  hint?: string;
}

interface ProjectMultiSelectProps {
  projects: ProjectOption[];
  /** Empty means "All Projects" */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  className?: string;
  /** Wording for other pickers built on this one (teams): "All Teams", "teams", ... */
  allLabel?: string;
  noun?: string;
  searchPlaceholder?: string;
  /** Fixed width, so choosing something never moves the other filters; long names are cut short */
  buttonWidthClass?: string;
}

export const ProjectMultiSelect: React.FC<ProjectMultiSelectProps> = ({
  projects,
  selectedIds,
  onChange,
  className = '',
  allLabel = 'All Projects',
  noun = 'project',
  searchPlaceholder = 'Search projects...',
  buttonWidthClass = 'w-[150px] justify-between',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  // Open towards the left when the button is near the right edge of the screen, so the list always
  // fits inside the window (it used to stick out and make the whole page scroll sideways)
  const [alignRight, setAlignRight] = useState(false);
  const toggleOpen = () => {
    if (!open && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      setAlignRight(rect.left + 256 > window.innerWidth - 12);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Forget the search each time the list closes
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const label = useMemo(() => {
    if (selectedIds.length === 0) return allLabel;
    if (selectedIds.length === 1) {
      const p = projects.find((x) => x.id === selectedIds[0]);
      return p ? `${p.name}${p.key ? ` (${p.key})` : ''}` : `1 ${noun}`;
    }
    return `${selectedIds.length} ${noun}s`;
  }, [selectedIds, projects]);

  const visible = projects.filter((p) => {
    const q = query.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || (p.key || '').toLowerCase().includes(q);
  });

  const toggle = (id: string) => {
    if (projects.find((p) => p.id === id)?.disabled) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Ticking every project is the same as "All Projects"
    // (with a single project there is nothing to merge into "all", so it stays picked)
    onChange(projects.length > 1 && next.size === projects.length ? [] : Array.from(next));
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={
          selectedIds.length > 1
            ? projects.filter((p) => selected.has(p.id)).map((p) => p.name).join(', ')
            : `Filter by ${noun} (pick one or several)`
        }
        className={`flex items-center gap-1.5 text-[11px] font-bold pl-2.5 pr-2 py-1 rounded-xl border focus:outline-none cursor-pointer ${buttonWidthClass} ${
          selectedIds.length > 0
            ? 'bg-brand-50 dark:bg-brand-950/40 border-brand-300 dark:border-brand-700 text-brand-700 dark:text-brand-300'
            : 'bg-[#f4f5f7] dark:bg-surface-800 border-[#d5d8de] dark:border-surface-700 text-[#121417] dark:text-white'
        }`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="w-3 h-3 shrink-0" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className={`absolute ${alignRight ? 'right-0' : 'left-0'} top-full mt-1.5 w-64 max-w-[calc(100vw-24px)] z-50 bg-white dark:bg-surface-850 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated p-2 text-xs`}
        >
          <div className="relative mb-1.5">
            <Search className="w-3 h-3 text-surface-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
              className="w-full pl-7 pr-2 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-[11px] text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <button
            type="button"
            role="option"
            aria-selected={selectedIds.length === 0}
            onClick={() => onChange([])}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 text-left font-semibold text-surface-800 dark:text-surface-100"
          >
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                selectedIds.length === 0 ? 'bg-brand-600 border-brand-600 text-white' : 'border-surface-300 dark:border-surface-600'
              }`}
            >
              {selectedIds.length === 0 && <Check className="w-3 h-3 stroke-[3]" />}
            </span>
            <span>{allLabel}</span>
          </button>

          <div className="max-h-56 overflow-y-auto mt-0.5 space-y-0.5">
            {visible.length === 0 ? (
              <div className="px-2 py-3 text-center text-surface-400">No {noun} matches.</div>
            ) : (
              visible.map((p) => {
                const isOn = selected.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={isOn}
                    onClick={() => toggle(p.id)}
                    disabled={p.disabled}
                    title={p.hint}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-surface-800 dark:text-surface-100 ${
                      p.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-surface-100 dark:hover:bg-surface-800'
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        isOn ? 'bg-brand-600 border-brand-600 text-white' : 'border-surface-300 dark:border-surface-600'
                      }`}
                    >
                      {isOn && <Check className="w-3 h-3 stroke-[3]" />}
                    </span>
                    <span className="truncate font-medium">{p.name}</span>
                    {(p.hint && p.disabled ? p.hint : p.key) && <span className="ml-auto text-[10px] font-mono text-surface-400 shrink-0">{p.disabled ? p.hint : p.key}</span>}
                  </button>
                );
              })
            )}
          </div>

          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1.5 w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-bold text-surface-500 hover:text-surface-800 dark:hover:text-white hover:bg-surface-100 dark:hover:bg-surface-800"
            >
              <X className="w-3 h-3" />
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};
